import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import {
  Person as PersonIcon,
  Support as SupportIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Message } from '../types';
import { endUserApiClient } from '../utils/api';
import { FileViewer } from './FileViewer';

interface MessageBubbleProps {
  message: Message;
  isCurrentUser?: boolean;
  showAvatar?: boolean;
  onMarkAsRead?: (messageId: string) => void;
  onEditMessage?: (messageId: string, newContent: string) => void;
  onDeleteMessage?: (messageId: string) => void;
  currentUserRole?: 'admin' | 'operator';
  disableEditDelete?: boolean;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isCurrentUser = false,
  showAvatar = true,
  onMarkAsRead,
  onEditMessage,
  onDeleteMessage,
  currentUserRole,
  disableEditDelete = false,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [editContent, setEditContent] = React.useState(message.content);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [viewerOpen, setViewerOpen] = React.useState(false);

  const menuOpen = Boolean(anchorEl);
  
  const getToken = (): string => {
    const adminToken = localStorage.getItem('adminToken');
    if (adminToken) return adminToken;
    const endUserSession = endUserApiClient.getSession();
    if (endUserSession?.token) return endUserSession.token;
    return '';
  };

  // ファイルURLを解決: S3ファイルはAPIエンドポイント経由、ローカルはトークン付与
  const getFilePreviewUrl = (msg: Message): string => {
    if (!msg.file_url) return '';

    if (msg.file_url.startsWith('s3:')) {
      return `/api/files/${msg.id}/preview?token=${encodeURIComponent(getToken())}`;
    }

    const separator = msg.file_url.includes('?') ? '&' : '?';
    return `${msg.file_url}${separator}token=${getToken()}`;
  };

  const getFileDownloadUrl = (msg: Message): string => {
    if (!msg.file_url) return '';

    if (msg.file_url.startsWith('s3:')) {
      return `/api/files/${msg.id}/download?token=${encodeURIComponent(getToken())}`;
    }

    const separator = msg.file_url.includes('?') ? '&' : '?';
    return `${msg.file_url}${separator}token=${getToken()}`;
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    setEditContent(message.content);
    setEditDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleEditSubmit = async () => {
    if (!editContent.trim() || !onEditMessage) return;
    
    setIsSubmitting(true);
    try {
      await onEditMessage(message.id, editContent.trim());
      setEditDialogOpen(false);
    } catch (error) {
      console.error('メッセージ編集エラー:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!onDeleteMessage) return;
    
    setIsSubmitting(true);
    try {
      await onDeleteMessage(message.id);
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('メッセージ削除エラー:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // 編集・削除権限のチェック
  const canEdit = () => {
    if (disableEditDelete) return false;
    if (message.message_type === 'system') return false;
    // CS側管理者、または送信者本人
    return currentUserRole === 'admin' || currentUserRole === 'operator' || isCurrentUser;
  };

  const canDelete = () => {
    if (disableEditDelete) return false;
    if (message.message_type === 'system') return false;
    // CS側管理者、または送信者本人
    return currentUserRole === 'admin' || currentUserRole === 'operator' || isCurrentUser;
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('ja-JP', {
        hour: '2-digit',
        minute: '2-digit',
      });
    } else {
      return date.toLocaleDateString('ja-JP', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  };

  const getDownloadExpiryInfo = (createdAt: string): { label: string; expired: boolean } => {
    const uploadDate = new Date(createdAt);
    const now = new Date();
    const isEndUser = !currentUserRole;
    const isAdmin = currentUserRole === 'admin';
    const expiryDays = isAdmin ? Infinity : isEndUser ? 7 : 90;

    if (expiryDays === Infinity) {
      return { label: 'ダウンロード期限: 無制限', expired: false };
    }

    const expiryDate = new Date(uploadDate.getTime() + expiryDays * 24 * 60 * 60 * 1000);
    const diffMs = expiryDate.getTime() - now.getTime();
    const expired = diffMs <= 0;

    const formatted = expiryDate.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });

    if (expired) {
      return { label: `ダウンロード期限切れ（${formatted}）`, expired: true };
    }

    const remainDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return { label: `ダウンロード期限: ${formatted}（残${remainDays}日）`, expired: false };
  };

  const getSenderIcon = () => {
    switch (message.sender_type) {
      case 'admin':
        return <SupportIcon fontSize="small" />;
      case 'end_user':
        return <PersonIcon fontSize="small" />;
      case 'system':
        return <PersonIcon fontSize="small" />;
      default:
        return <PersonIcon fontSize="small" />;
    }
  };

  const getSenderColor = () => {
    switch (message.sender_type) {
      case 'admin':
        return '#1976d2';
      case 'end_user':
        return '#2e7d32';
      case 'system':
        return '#757575';
      default:
        return '#757575';
    }
  };

  const getMessageBackgroundColor = () => {
    if (message.message_type === 'system') {
      return 'transparent';
    }
    return '#ffffff';
  };

  const getMessageTextColor = () => {
    if (message.message_type === 'system') {
      return '#757575';
    }
    return '#000000';
  };

  // システムメッセージの場合は中央配置
  if (message.message_type === 'system') {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          my: 2,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: '#757575',
            fontSize: '0.75rem',
            backgroundColor: 'transparent',
            px: 2,
            py: 0.5,
            textAlign: 'center',
          }}
        >
          {message.content}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isCurrentUser ? 'row-reverse' : 'row',
        mb: 2,
        alignItems: 'flex-start',
      }}
    >
      {/* アバター */}
      {showAvatar && (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            bgcolor: getSenderColor(),
            mx: 1,
            fontSize: '0.875rem',
          }}
        >
          {getSenderIcon()}
        </Avatar>
      )}

      {/* メッセージ本体 */}
      <Box
        sx={{
          maxWidth: '70%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isCurrentUser ? 'flex-end' : 'flex-start',
        }}
        className="message-bubble"
      >
        {/* 送信者名 */}
        <Typography
          variant="caption"
          sx={{
            color: isCurrentUser ? '#1976d2' : '#757575',
            fontWeight: 600,
            mb: 0.5,
            px: 1,
          }}
        >
          {message.sender_name}
        </Typography>

        {/* メッセージバブル */}
        <Paper
          elevation={1}
          sx={{
            p: 1.5,
            backgroundColor: getMessageBackgroundColor(),
            borderRadius: isCurrentUser 
              ? '18px 18px 4px 18px'  // 右下を尖らせる
              : '18px 18px 18px 4px', // 左下を尖らせる
            border: 'none',
            position: 'relative',
          }}
        >
          {/* 操作メニューボタン */}
          {(canEdit() || canDelete()) && (
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                opacity: 0,
                transition: 'opacity 0.2s',
                '&:hover': { opacity: 1 },
                '.message-bubble:hover &': { opacity: 1 },
              }}
            >
              <IconButton
                size="small"
                onClick={handleMenuClick}
                sx={{ p: 0.25 }}
              >
                <MoreVertIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}

          {/* メッセージ内容 */}
          <Typography
            variant="body2"
            sx={{
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              lineHeight: 1.4,
              color: getMessageTextColor(),
            }}
          >
            {/* URLを自動リンク化 */}
            {message.content.split(/(https?:\/\/[^\s]+)/g).map((part, idx) => {
              if (/^https?:\/\//.test(part)) {
                return (
                  <a key={idx} href={part} target="_blank" rel="noopener noreferrer">
                    {part}
                  </a>
                );
              }
              return <span key={idx}>{part}</span>;
            })}
            {message.is_edited && (
              <Typography
                component="span"
                variant="caption"
                sx={{
                  color: '#757575',
                  fontSize: '0.7rem',
                  fontStyle: 'italic',
                  ml: 1,
                }}
              >
                (編集済み)
              </Typography>
            )}
          </Typography>

          {/* ファイル情報 */}
          {message.file_url && (() => {
            const expiryInfo = getDownloadExpiryInfo(message.created_at);
            return (
              <Box sx={{ mt: 1 }}>
                {/\.(png|jpe?g|gif|webp)$/i.test(message.file_name || message.file_url) ? (
                  <Box
                    sx={{
                      display: 'block',
                      maxWidth: '300px',
                      borderRadius: 1,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      '&:hover': { opacity: 0.9 }
                    }}
                    onClick={() => setViewerOpen(true)}
                  >
                    <img
                      src={getFilePreviewUrl(message)}
                      alt={message.file_name || '添付画像'}
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                      onError={(e) => {
                        console.error('画像読み込みエラー:', message.file_url);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{ p: 1, backgroundColor: '#f5f5f5', borderRadius: 1, cursor: 'pointer', '&:hover': { backgroundColor: '#eee' } }}
                    onClick={() => setViewerOpen(true)}
                  >
                    <Typography variant="caption" color="primary" sx={{ textDecoration: 'underline' }}>
                      {message.file_name || '添付ファイル'}
                    </Typography>
                  </Box>
                )}
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mt: 0.3,
                    fontSize: '0.7rem',
                    color: expiryInfo.expired ? '#d32f2f' : '#888',
                  }}
                >
                  {expiryInfo.label}
                </Typography>
              </Box>
            );
          })()}

          {/* 時刻 */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.7rem',
              }}
            >
              {formatTime(message.created_at)}
            </Typography>
          </Box>
        </Paper>
      </Box>

      {/* 操作メニュー */}
      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {canEdit() && (
          <MenuItem onClick={handleEditClick}>
            <EditIcon sx={{ mr: 1, fontSize: 16 }} />
            編集
          </MenuItem>
        )}
        {canDelete() && (
          <MenuItem onClick={handleDeleteClick}>
            <DeleteIcon sx={{ mr: 1, fontSize: 16 }} />
            削除
          </MenuItem>
        )}
      </Menu>

      {/* 編集ダイアログ */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>メッセージを編集</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            multiline
            rows={4}
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            variant="outlined"
            placeholder="メッセージを入力..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleEditSubmit}
            variant="contained"
            disabled={!editContent.trim() || isSubmitting}
          >
            {isSubmitting ? '更新中...' : '更新'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 削除確認ダイアログ */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>メッセージを削除</DialogTitle>
        <DialogContent>
          <Typography>
            このメッセージを削除しますか？この操作は取り消せません。
          </Typography>
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              "{message.content.length > 100 ? message.content.substring(0, 100) + '...' : message.content}"
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isSubmitting}
          >
            {isSubmitting ? '削除中...' : '削除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ファイルビューアー */}
      {message.file_url && (
        <FileViewer
          open={viewerOpen}
          onClose={() => setViewerOpen(false)}
          messageId={message.id}
          fileName={message.file_name || undefined}
        />
      )}
    </Box>
  );
};

export default MessageBubble; 